export function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">About OpenApps</h1>
          <p className="text-xl text-gray-600 mb-8">
            A secure connective layer that unlocks the internet for AI agents—enabling them to act safely in the real world.
          </p>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Our Mission</h2>
            <p className="text-gray-700 mb-6">
              <strong>To Enable AI Agents to Act in the Real World.</strong> OpenApps.ai serves as a secure connective layer 
              that unlocks the internet for AI agents—turning any standard web infra(REST APIs, DBs, and other targets) and systems into safe, orchestrated capabilities so agents 
              can securely act in the real world. By standardizing security, identity, and observability across tools and protocols, 
              we aim to power a new generation of trustworthy, scalable agent ecosystems for every company.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Our Vision</h2>
            <p className="text-gray-700 mb-6">
              OpenApps vision is to become the foundational infrastructure that enables 
              the next generation of AI applications to interact safely and effectively with the existing digital world.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">The Challenge</h2>
            <p className="text-gray-700 mb-4">
              AI agents need to interact with real-world systems to be truly useful, but this creates significant challenges:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Security Risks:</strong> Agents accessing APIs without proper identity and access controls</li>
              <li><strong>Integration Complexity:</strong> Each API requires custom integration work and maintenance</li>
              <li><strong>Lack of Observability:</strong> No unified way to monitor and audit agent actions across systems</li>
              <li><strong>Inconsistent Protocols:</strong> Different APIs use different authentication, formats, and protocols</li>
              <li><strong>Compliance Concerns:</strong> Ensuring agent actions meet regulatory and security requirements</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Our Solution</h2>
            <p className="text-gray-700 mb-4">
              OpenApps provides a comprehensive platform that addresses these challenges:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Automated MCP Generation:</strong> Convert any OpenAPI spec into a secure, agent-ready MCP server</li>
              <li><strong>Unified Security Layer:</strong> Standardized authentication, authorization, and rate limiting across all integrations</li>
              <li><strong>Identity Management:</strong> Granular access controls and identity propagation for agent actions</li>
              <li><strong>Comprehensive Observability:</strong> Real-time monitoring, logging, and auditing of all agent interactions</li>
              <li><strong>Protocol Standardization:</strong> Consistent interface regardless of underlying API complexity</li>
              <li><strong>Enterprise Compliance:</strong> SOC2, GDPR, and industry-specific compliance features built-in</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Platform Architecture</h2>
            <p className="text-gray-700 mb-4">
              Our platform is built on enterprise-grade infrastructure designed for scale and security:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Cloud-native architecture with global distribution</li>
              <li>Zero-trust security model with end-to-end encryption</li>
              <li>Real-time threat detection and automated response</li>
              <li>Scalable infrastructure handling millions of agent interactions</li>
              <li>Multi-region deployment for high availability and compliance</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Who We Serve</h2>
            <p className="text-gray-700 mb-4">OpenApps empowers organizations across industries:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Enterprise Technology Teams:</strong> Safely deploy AI agents across existing infrastructure</li>
              <li><strong>AI Agent Developers:</strong> Focus on agent intelligence, not integration complexity</li>
              <li><strong>API Providers:</strong> Make APIs agent-ready without rebuilding existing systems</li>
              <li><strong>Compliance Teams:</strong> Ensure agent actions meet regulatory and security requirements</li>
              <li><strong>Platform Companies:</strong> Enable ecosystem partners to integrate AI capabilities safely</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Market Impact</h2>
            <p className="text-gray-700 mb-6">
              As AI agents become ubiquitous, the need for a secure, standardized connective layer becomes critical. 
              OpenApps is positioned at the intersection of AI advancement and enterprise infrastructure needs, 
              enabling organizations to harness agent capabilities while maintaining security, compliance, and control.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Our Commitment</h2>
            <p className="text-gray-700 mb-6">
              We're committed to building the foundational infrastructure that enables trustworthy AI agent ecosystems. 
              Security, reliability, and developer experience are at the core of everything we build. We believe that 
              AI agents should enhance human capabilities while operating within clear, auditable, and secure boundaries.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Get Started</h2>
            <p className="text-gray-700 mb-4">
              Ready to enable your AI agents to act securely in the real world? 
              <a href="/auth/register" className="text-blue-600 hover:text-blue-800 ml-1">Start building for free</a> 
              or <a href="/contact" className="text-blue-600 hover:text-blue-800 ml-1">contact our team</a> 
              to discuss enterprise deployment.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function CareersPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Join Our Team</h1>
          <p className="text-xl text-gray-600 mb-8">
            Help us enable AI agents to act safely in the real world. We're looking for passionate engineers and creators.
          </p>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Why OpenApps?</h2>
            <p className="text-gray-700 mb-6">
              Join a team that's enabling AI agents to act safely in the real world. We're building the foundational 
              infrastructure that connects AI agents to the internet—creating trustworthy, scalable agent ecosystems 
              for every company.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Our Culture</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Innovation First:</strong> We encourage experimentation and learning from failure</li>
              <li><strong>Remote-First:</strong> Work from anywhere with flexible hours and async collaboration</li>
              <li><strong>Developer-Centric:</strong> Built by developers, for developers - we understand the craft</li>
              <li><strong>Impact Driven:</strong> Every feature we build directly impacts thousands of developers</li>
              <li><strong>Continuous Learning:</strong> Stay ahead with conference budgets and learning stipends</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Benefits & Perks</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">💰 Compensation</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>Competitive salary + equity</li>
                  <li>Performance bonuses</li>
                  <li>Annual salary reviews</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🏥 Health & Wellness</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>Premium health insurance</li>
                  <li>Mental health support</li>
                  <li>Fitness reimbursements</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">⏰ Work-Life Balance</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>Unlimited PTO policy</li>
                  <li>Flexible working hours</li>
                  <li>4-day work week options</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">📚 Growth</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>$2k annual learning budget</li>
                  <li>Conference attendance</li>
                  <li>Mentorship programs</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Open Positions</h2>
            
            <div className="space-y-6 mb-8">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Senior Backend Engineer</h3>
                <p className="text-gray-600 mb-3">Remote • Full-time</p>
                <p className="text-gray-700 mb-4">
                  Build scalable infrastructure for MCP server generation and deployment. 
                  Experience with Go, Kubernetes, and OpenAPI required.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">Go</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">Kubernetes</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">OpenAPI</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">Docker</span>
                </div>
                <a href="mailto:careers@openapps.ai?subject=Senior Backend Engineer" 
                   className="text-blue-600 hover:text-blue-800 font-medium">Apply Now →</a>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Frontend Engineer</h3>
                <p className="text-gray-600 mb-3">Remote • Full-time</p>
                <p className="text-gray-700 mb-4">
                  Create beautiful, intuitive interfaces for developer tools. 
                  Experience with React, TypeScript, and developer UX preferred.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">React</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">TypeScript</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Tailwind</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">UX Design</span>
                </div>
                <a href="mailto:careers@openapps.ai?subject=Frontend Engineer" 
                   className="text-blue-600 hover:text-blue-800 font-medium">Apply Now →</a>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">DevRel Engineer</h3>
                <p className="text-gray-600 mb-3">Remote • Full-time</p>
                <p className="text-gray-700 mb-4">
                  Build relationships with the developer community. Create content, speak at conferences, 
                  and help developers succeed with OpenApps.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">Technical Writing</span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">Public Speaking</span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">APIs</span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">AI/ML</span>
                </div>
                <a href="mailto:careers@openapps.ai?subject=DevRel Engineer" 
                   className="text-blue-600 hover:text-blue-800 font-medium">Apply Now →</a>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Application Process</h2>
            <ol className="list-decimal pl-6 mb-6 text-gray-700 space-y-2">
              <li><strong>Apply:</strong> Send your resume and a brief note about why you're excited about OpenApps</li>
              <li><strong>Initial Chat:</strong> 30-minute conversation with our team to get to know you</li>
              <li><strong>Technical Interview:</strong> Practical discussion about your experience and approach</li>
              <li><strong>Team Meet:</strong> Meet the people you'd be working with directly</li>
              <li><strong>Offer:</strong> If it's a good fit, we'll make you an offer within 24 hours</li>
            </ol>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Don't See Your Role?</h2>
            <p className="text-gray-700 mb-4">
              We're always looking for exceptional talent. If you're passionate about AI, APIs, and developer tools, 
              we'd love to hear from you even if we don't have a specific opening.
            </p>
            <p className="text-gray-700">
              Email us at <a href="mailto:careers@openapps.ai" className="text-blue-600 hover:text-blue-800">careers@openapps.ai</a> 
              with your background and what excites you about OpenApps.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function PressPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Press & Media</h1>
          <p className="text-xl text-gray-600 mb-8">
            Resources for journalists, analysts, and media professionals covering OpenApps and the AI-API integration space.
          </p>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Company Overview</h2>
            <p className="text-gray-700 mb-6">
              OpenApps is the secure connective layer that unlocks the internet for AI agents, enabling them to act safely 
              in the real world. Founded in 2024, we're building the foundational infrastructure that turns any API or system 
              into safe, orchestrated capabilities for trustworthy AI agent ecosystems.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Key Facts</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li><strong>Founded:</strong> 2024</li>
              <li><strong>Mission:</strong> Enable AI agents to act safely in the real world through secure infrastructure</li>
              <li><strong>Target Market:</strong> AI agent developers, enterprise technology teams, and platform companies</li>
              <li><strong>Technology:</strong> Secure connective layer with identity management, observability, and protocol standardization</li>
              <li><strong>Deployment:</strong> Cloud-native platform with global infrastructure</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Recent News</h2>
            <div className="space-y-6 mb-8">
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">OpenApps Launches Public Platform</h3>
                <p className="text-gray-600 text-sm mb-2">{new Date().toLocaleDateString()}</p>
                <p className="text-gray-700">
                  OpenApps announces the public launch of its secure connective layer platform, 
                  enabling AI agents to safely interact with APIs and systems across the internet.
                </p>
              </div>

              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Multi-Protocol Support Release</h3>
                <p className="text-gray-600 text-sm mb-2">{new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                <p className="text-gray-700">
                  Enhanced protocol standardization with SSE, WebSocket, and HTTP support ensures 
                  seamless integration across diverse AI agent architectures and platforms.
                </p>
              </div>

              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Enterprise Security Features</h3>
                <p className="text-gray-600 text-sm mb-2">{new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                <p className="text-gray-700">
                  Launch of comprehensive security layer with identity management, access controls, 
                  and enterprise-grade compliance features for safe agent operations.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Market Position</h2>
            <p className="text-gray-700 mb-4">
              OpenApps addresses the critical need for secure AI agent infrastructure as organizations seek to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Deploy AI agents that can safely interact with existing systems and APIs</li>
              <li>Maintain security, compliance, and observability across agent operations</li>
              <li>Standardize identity and access management for AI agent ecosystems</li>
              <li>Enable trustworthy, auditable AI agent interactions at enterprise scale</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Industry Impact</h2>
            <p className="text-gray-700 mb-6">
              As AI agents become more capable and autonomous, the need for secure, standardized infrastructure becomes critical. 
              OpenApps enables the safe deployment of AI agents across existing systems, accelerating enterprise AI adoption 
              while maintaining security, compliance, and control.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Leadership Quotes</h2>
            <blockquote className="border-l-4 border-gray-300 pl-6 mb-6 italic text-gray-700">
              "AI agents need to act in the real world to be truly valuable, but this requires unprecedented 
              levels of security and trust. OpenApps provides the foundational infrastructure that makes safe, 
              scalable AI agent deployment possible across any organization."
              <footer className="mt-2 text-gray-600">— OpenApps Leadership Team</footer>
            </blockquote>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Press Resources</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">📄 Assets</h3>
                <ul className="text-gray-700 space-y-1">
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">Company Logo (PNG, SVG)</a></li>
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">Product Screenshots</a></li>
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">Leadership Photos</a></li>
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">Brand Guidelines</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 Resources</h3>
                <ul className="text-gray-700 space-y-1">
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">Company Fact Sheet</a></li>
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">Technical Architecture</a></li>
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">Use Case Studies</a></li>
                  <li><a href="#" className="text-blue-600 hover:text-blue-800">API Integration Guide</a></li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Media Contact</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-2">
                <strong>For press inquiries, interviews, and media requests:</strong>
              </p>
              <p className="text-gray-700 mb-1">
                Email: <a href="mailto:press@openapps.ai" className="text-blue-600 hover:text-blue-800">press@openapps.ai</a>
              </p>
              <p className="text-gray-700 mb-4">
                Response time: Within 24 hours for time-sensitive requests
              </p>
              <p className="text-gray-700 text-sm">
                Please include your publication, deadline, and specific information needs in your inquiry.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function PartnersPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Partner Ecosystem</h1>
          <p className="text-xl text-gray-600 mb-8">
            Join our growing ecosystem of technology partners, integrators, and solution providers.
          </p>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Partnership Opportunities</h2>
            <p className="text-gray-700 mb-6">
              OpenApps partners with leading technology companies, system integrators, and service providers 
              to deliver comprehensive AI-API integration solutions to our mutual customers.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Partner Types</h2>
            
            <div className="space-y-8 mb-8">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">🤖 AI Platform Partners</h3>
                <p className="text-gray-700 mb-4">
                  Leading AI and LLM platforms that integrate with OpenApps MCP servers to provide 
                  enhanced capabilities to their users.
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Native MCP client integrations</li>
                  <li>Joint go-to-market strategies</li>
                  <li>Co-developed solutions and templates</li>
                  <li>Shared documentation and tutorials</li>
                </ul>
                <p className="text-sm text-gray-600">
                  <strong>Ideal for:</strong> AI platforms, LLM providers, agent frameworks, AI development tools
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">🔧 Technology Partners</h3>
                <p className="text-gray-700 mb-4">
                  Technology companies whose APIs and services benefit from AI integration through OpenApps.
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Pre-built MCP server templates</li>
                  <li>Optimized authentication flows</li>
                  <li>Joint customer success programs</li>
                  <li>Technical collaboration and support</li>
                </ul>
                <p className="text-sm text-gray-600">
                  <strong>Ideal for:</strong> SaaS providers, API companies, cloud platforms, data services
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">🏢 System Integrators</h3>
                <p className="text-gray-700 mb-4">
                  Consulting firms and system integrators who help enterprises implement AI solutions.
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Partner certification programs</li>
                  <li>Sales and technical training</li>
                  <li>Lead sharing and referral programs</li>
                  <li>Joint enterprise engagements</li>
                </ul>
                <p className="text-sm text-gray-600">
                  <strong>Ideal for:</strong> Consulting firms, system integrators, digital agencies, enterprise solution providers
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">🎓 Educational Partners</h3>
                <p className="text-gray-700 mb-4">
                  Educational institutions and training providers helping developers learn AI-API integration.
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Free access to OpenApps for educational use</li>
                  <li>Curriculum development support</li>
                  <li>Guest lecture and workshop opportunities</li>
                  <li>Student project sponsorships</li>
                </ul>
                <p className="text-sm text-gray-600">
                  <strong>Ideal for:</strong> Universities, coding bootcamps, online learning platforms, developer training organizations
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Partner Benefits</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🚀 Business Growth</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>Access to our growing customer base</li>
                  <li>Joint marketing and co-selling opportunities</li>
                  <li>Revenue sharing programs</li>
                  <li>Priority placement in our partner directory</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🛠️ Technical Support</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>Dedicated partner engineering support</li>
                  <li>Early access to new features and APIs</li>
                  <li>Custom integration development</li>
                  <li>Technical documentation and SDKs</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">📈 Market Access</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>Co-marketing campaigns and case studies</li>
                  <li>Joint conference presentations</li>
                  <li>Thought leadership opportunities</li>
                  <li>Customer introduction programs</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🎯 Enablement</h3>
                <ul className="list-disc pl-4 text-gray-700">
                  <li>Sales and technical training programs</li>
                  <li>Partner certification courses</li>
                  <li>Marketing collateral and sales tools</li>
                  <li>Dedicated partner success managers</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Success Stories</h2>
            <div className="space-y-6 mb-8">
              <blockquote className="border-l-4 border-blue-500 pl-6 italic text-gray-700">
                "Partnering with OpenApps has accelerated our AI integration timeline by months. 
                Their automated MCP generation means our customers can connect our APIs to AI applications 
                in minutes, not weeks."
                <footer className="mt-2 text-gray-600">— Technology Partner, SaaS Platform</footer>
              </blockquote>

              <blockquote className="border-l-4 border-green-500 pl-6 italic text-gray-700">
                "The OpenApps partner program has opened new revenue streams for our consulting practice. 
                We can now offer AI integration services with confidence, backed by enterprise-grade infrastructure."
                <footer className="mt-2 text-gray-600">— System Integrator Partner</footer>
              </blockquote>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Partner Requirements</h2>
            <p className="text-gray-700 mb-4">
              We're looking for partners who share our commitment to excellence and customer success:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Proven track record in your respective market</li>
              <li>Commitment to customer success and support</li>
              <li>Technical expertise in APIs, AI, or enterprise solutions</li>
              <li>Willingness to invest in joint go-to-market activities</li>
              <li>Alignment with OpenApps values and mission</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Get Started</h2>
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Ready to Partner with OpenApps?</h3>
              <p className="text-gray-700 mb-4">
                Join our partner ecosystem and help shape the future of AI-API integration. 
                We're actively seeking partners who want to grow together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="mailto:partners@openapps.ai?subject=Partnership Inquiry" 
                   className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors text-center">
                  Become a Partner
                </a>
                <a href="mailto:partners@openapps.ai?subject=Partnership Information Request" 
                   className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors text-center">
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function BlogPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <button 
            onClick={() => window.history.back()} 
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Blog</h1>
          <p className="text-xl text-gray-600 mb-8">Insights and updates from the OpenApps team.</p>
          <p className="text-gray-700">Coming soon - stay tuned for insights on AI agents, security, and platform updates.</p>
        </div>
      </div>
    </div>
  )
}


